/**
 * The order albums are listed in.
 *
 * Newest year first, everywhere: the sidebar, the picker, the albums screen.
 * A photo library is browsed by "when was that" far more often than by "what
 * was it called", and an album for a 2019 trip uploaded last weekend belongs
 * with 2019 rather than at the top of everything.
 *
 * Undated albums go last rather than first. They are the ones nobody has got
 * round to filling in, and stacking them above the trips somebody *has* dated
 * would make the feature look broken to the person who just used it.
 *
 * Within a year, whatever the caller was already ordering by decides — most
 * recently touched in the picker, the group's own arrangement in the sidebar.
 * Year is a coarse sort, not a replacement for the fine one.
 */

export interface Dated {
  year: number | null;
}

/**
 * Compares two albums by year alone: negative if `a` should be listed first.
 *
 * Returns 0 for two albums of the same year — including two undated ones — so
 * it composes with `Array.prototype.sort`'s stability and the caller's own
 * tiebreaker rather than overruling them.
 */
export function byYear(a: Dated, b: Dated): number {
  if (a.year === b.year) return 0;
  if (a.year === null) return 1;
  if (b.year === null) return -1;
  return b.year - a.year;
}

/** `byYear`, then the caller's existing order. */
export function byYearThen<T extends Dated>(
  next: (a: T, b: T) => number,
): (a: T, b: T) => number {
  return (a, b) => byYear(a, b) || next(a, b);
}
